/**
 * Pure, render-free derivations for the session surface.
 *
 * Everything here is total and side-effect free so attribution mapping,
 * current-session detection, lifecycle state and revocation eligibility
 * can be unit tested without a DOM, a router or a network.
 *
 * Security contract:
 *  - token hashes, session tokens, secrets, credential material and PRF
 *    output are never read, derived or returned;
 *  - attribution arrives already normalized; the raw User-Agent is not
 *    part of the read model and must never be reconstructed here.
 */
import type {SessionRow} from "../../lib/types";

const UUID_RE=/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isValidSessionId(value:string|null|undefined):value is string{
  return typeof value==="string"&&UUID_RE.test(value.trim());
}

/* ------------------------------------------------------------------ lifecycle */

export type SessionState="ACTIVE"|"EXPIRED"|"REVOKED";

export type StateToneKind="positive"|"attention"|"critical"|"neutral";

/**
 * Lifecycle state.
 *
 * Revoked wins over expired: an administrator who ends a session expects
 * the record to read as revoked even if it had also aged out.
 */
export function sessionState(
  session:Pick<SessionRow,"revoked_at"|"active">,
):SessionState{
  if(session.revoked_at)return "REVOKED";
  return session.active?"ACTIVE":"EXPIRED";
}

export function stateTone(state:SessionState):StateToneKind{
  if(state==="ACTIVE")return "positive";
  if(state==="REVOKED")return "critical";
  return "attention";
}

/* ---------------------------------------------------------------- attribution */

/** Canonical machine values, localized by the caller and never mutated. */
export type AuthMethod="PASSKEY"|"DESKTOP_HANDOFF"|"PASSWORD"|"UNKNOWN";

export function authMethod(
  value:string|null|undefined,
):AuthMethod{
  const raw=(value??"").toUpperCase();
  if(raw==="PASSKEY")return "PASSKEY";
  if(raw==="DESKTOP_HANDOFF")return "DESKTOP_HANDOFF";
  if(raw==="PASSWORD")return "PASSWORD";
  return "UNKNOWN";
}

export type ClientClass="DESKTOP"|"MOBILE"|"TABLET"|"UNKNOWN";

export function clientClass(
  value:string|null|undefined,
):ClientClass{
  const raw=(value??"").toUpperCase();
  if(raw==="DESKTOP")return "DESKTOP";
  if(raw==="MOBILE")return "MOBILE";
  if(raw==="TABLET")return "TABLET";
  return "UNKNOWN";
}

/**
 * The headline descriptor, for example "Chrome · Linux".
 *
 * Returns the raw family names with no separator when either side is
 * unknown, so the UI can render what it actually knows instead of
 * showing a placeholder. Callers localize the family via a display map.
 */
export function clientDescriptor(session:{
  browser_family:string|null;
  os_family:string|null;
}):string{
  const browser=(session.browser_family??"").trim();
  const os=(session.os_family??"").trim();
  if(browser&&os)return `${browser} · ${os}`;
  return browser||os;
}

export interface SessionDeviceView{
  descriptor:string;
  hasDescriptor:boolean;
  deviceLabel:string|null;
  clientClass:ClientClass;
  authMethod:AuthMethod;
}

export function deviceView(session:SessionRow):SessionDeviceView{
  const descriptor=clientDescriptor(session);
  const deviceLabel=(session.device_label??session.credential_label??"").trim();
  return{
    descriptor,
    hasDescriptor:descriptor.length>0,
    deviceLabel:deviceLabel||null,
    clientClass:clientClass(session.client_class),
    authMethod:authMethod(session.auth_method),
  };
}

/**
 * Rows are ordered so the operator's own session appears first, then
 * live sessions, then the most recently seen history. Ordering is stable
 * and never mutates the source array.
 */
export function sortSessions(sessions:SessionRow[]):SessionRow[]{
  const rank=(s:SessionRow):number=>{
    if(s.is_current)return 0;
    return sessionState(s)==="ACTIVE"?1:2;
  };
  return [...sessions].sort((a,b)=>
    rank(a)-rank(b)
    ||(b.last_seen_at??"").localeCompare(a.last_seen_at??"")
    ||(a.session_id??"").localeCompare(b.session_id??""));
}

/* --------------------------------------------------------------- current-ness */

/**
 * Current-session detection.
 *
 * ``is_current`` is the authoritative marker, derived by the database
 * from the caller's own verified session. A matching ``session_id`` is
 * accepted as a defensive fallback but never inferred from browser, OS
 * or timing, because those are not unique.
 */
export function isCurrentSession(
  session:Pick<SessionRow,"is_current"|"session_id">,
  currentSessionId:string|null|undefined,
):boolean{
  if(session.is_current)return true;
  if(!currentSessionId)return false;
  return Boolean(session.session_id)&&session.session_id===currentSessionId;
}

/* -------------------------------------------------------------------- revoke */

export type RevokeEligibility="allowed"|"current"|"inactive"|"unknown";

/**
 * Whether an administrator may revoke this session.
 *
 * The backend refuses self-revoke and is idempotent for sessions that
 * already ended, so the UI mirrors those rules instead of offering an
 * action that would fail.
 */
export function revokeEligibility(
  session:SessionRow,
):RevokeEligibility{
  if(!session.session_id||!isValidSessionId(session.session_id))return "unknown";
  if(session.is_current)return "current";
  if(sessionState(session)!=="ACTIVE")return "inactive";
  return "allowed";
}

export function canRevoke(session:SessionRow):boolean{
  return revokeEligibility(session)==="allowed";
}

/* ------------------------------------------------------------------- relative */

/**
 * Coarse relative time bucket, used for the "last active" line.
 *
 * Deliberately coarse: a session screen does not need second-level
 * precision, and a coarse bucket cannot be used to fingerprint timing.
 */
export type RelativeBucket="just-now"|"minutes"|"hours"|"days"|"unknown";

export function relativeBucket(
  value:string|null|undefined,
  nowMs:number,
):RelativeBucket{
  if(!value)return "unknown";
  const then=Date.parse(value);
  if(Number.isNaN(then))return "unknown";
  const deltaMs=nowMs-then;
  if(deltaMs<0)return "unknown";
  const minutes=Math.floor(deltaMs/60000);
  if(minutes<2)return "just-now";
  if(minutes<60)return "minutes";
  const hours=Math.floor(minutes/60);
  if(hours<24)return "hours";
  return "days";
}

export function relativeMinutes(
  value:string|null|undefined,
  nowMs:number,
):number|null{
  if(!value)return null;
  const then=Date.parse(value);
  if(Number.isNaN(then))return null;
  const deltaMs=nowMs-then;
  if(deltaMs<0)return null;
  return Math.floor(deltaMs/60000);
}

/** Expiry in whole days, used for "expires tomorrow" phrasing. */
export function expiryDays(
  value:string|null|undefined,
  nowMs:number,
):number|null{
  if(!value)return null;
  const then=Date.parse(value);
  if(Number.isNaN(then))return null;
  const deltaMs=then-nowMs;
  return Math.floor(deltaMs/86400000);
}

/* ------------------------------------------------------------------ redaction */

/**
 * The set of read-model fields the session surface is allowed to render.
 *
 * Enforced as a test so a future change cannot quietly start reading a
 * secret-bearing field.
 */
export const ALLOWED_SESSION_FIELDS:readonly string[]=[
  "session_id", "subject_id", "subject_name", "created_at", "last_seen_at",
  "expires_at", "revoked_at", "active", "device_label", "client_class",
  "browser_family", "os_family", "auth_method", "ip_address",
  "device_posture_id", "posture_trust_state", "posture_platform",
  "posture_last_verified_at", "posture_recovery_class", "state",
  "credential_label", "session_total", "active_session_total", "is_current",
];

export const FORBIDDEN_SESSION_FIELDS:readonly string[]=[
  "token_hash", "token", "session_token", "secret", "wrapped_key",
  "private_key", "client_data", "attestation", "prf_result", "prf_output",
];